import React, {useState} from "react";
import {Check, Sparkles, X} from "lucide-react";
import {PlugKind, PlugsView} from "../bid/bidding_model";
import {Popover, PopoverContent, PopoverTrigger} from "../ui/popover";
import {Input} from "../ui/input";
import {Button} from "../ui/button";
import {useToast} from "../ui/use-toast";
import {moneyFormatter} from "../../lib/formatters";
import {KindSelect} from "./PlugCell";
import {kindLabel, parseAmount, signedMoney} from "./plugs";
import {
    BidLevelingState, LevelingBidder, SCOPE_STATUS, SUGGESTION_LINE, SUGGESTION_TAG, SuggestedPlug
} from "./ai_leveling";


// claude refers to bidders by key ("B2") - show their names
export function withNames(text: string, bidders: LevelingBidder[]): string {
    return text.replace(/\bB\d+\b/g, (key) => bidders.find((b) => b.key === key)?.company_name ?? key);
}

// same look as the review's lists (SmartFeedback imports this file, so not shared from there)
function NoteList({title, items}: { title: string, items: string[] }) {
    if (!items || items.length === 0) return null;
    return <div className="mb-2">
        <div className="font-semibold">{title}</div>
        <ul className="list-disc ml-5">
            {items.map((x, i) => <li key={i}>{x}</li>)}
        </ul>
    </div>
}

const valueText = (v: number | string | null) =>
    typeof v === "number" ? moneyFormatter.format(v) : (v === null || v === "" ? "—" : String(v));


type SuggestionPopoverProps = {
    suggestion: SuggestedPlug;
    bidders: LevelingBidder[];
    // resolves when the plug is made - the popover closes
    onAccept: (edits: { amount: number, note: string, kind: PlugKind }) => Promise<any>;
    onReject: () => void;
    children: React.ReactElement;
}

/**
 * One AI plug suggestion - why, the other bidders' values on the line, and accept (with edits) / reject.
 * Accepting makes it a plug like any other (source ai).
 * */
export function SuggestionPopover({suggestion, bidders, onAccept, onReject, children}: SuggestionPopoverProps) {
    const {toast} = useToast();
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState(String(suggestion.amount));
    const [note, setNote] = useState(suggestion.note);
    const [kind, setKind] = useState<PlugKind>(suggestion.kind);
    const others = Object.keys(suggestion.others);

    const onAcceptClick = () => {
        const value = parseAmount(amount);
        if (value === null) {
            toast({title: "Enter an amount", description: "eg 15000 or -5000", variant: "destructive"});
            return;
        }
        onAccept({amount: value, note, kind}).then(() => setOpen(false)).catch(() => null);
    }

    return <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
            {children}
        </PopoverTrigger>
        <PopoverContent className="w-[30rem] p-3 text-xs" align="end">
            <div className="text-gray-500 mb-1">
                AI suggestion · {suggestion.cell_id != null ? suggestion.label : "base bid"}
            </div>
            <div className="mb-2">{withNames(suggestion.reason, bidders)}</div>
            {others.length > 0 &&
                <div className="text-gray-500 mb-2">
                    On this line: {others.map((k) =>
                    `${withNames(k, bidders)} ${valueText(suggestion.others[k])}`).join(" · ")}
                </div>}
            <div className="flex gap-1 items-center">
                <KindSelect value={kind} onChange={setKind}/>
                <Input className="h-7 w-24 shrink-0 text-xs text-right" value={amount}
                       onChange={(e) => setAmount(e.target.value)}/>
                <Input className="h-7 text-xs" value={note} placeholder="Note"
                       onChange={(e) => setNote(e.target.value)}/>
                <Button size="sm" className="h-7 px-2" onClick={onAcceptClick} title="Accept - adds the plug">
                    <Check className="w-3 h-3"/>
                </Button>
                <Button variant="ghost" size="sm" className="h-7 px-2" title="Reject"
                        onClick={() => {
                            onReject();
                            setOpen(false);
                        }}>
                    <X className="w-3 h-3"/>
                </Button>
            </div>
        </PopoverContent>
    </Popover>
}


// the trigger for a suggestion - kind, amount, note
export const SuggestionChip = React.forwardRef<HTMLButtonElement, { suggestion: SuggestedPlug }>(
    ({suggestion, ...props}, ref) =>
        <button ref={ref} type="button" {...props}
                className="flex gap-1 items-center text-left hover:underline">
            <span className={"inline-flex items-center gap-0.5 rounded px-1 text-[10px] font-medium " + SUGGESTION_TAG}>
                <Sparkles className="w-3 h-3" aria-hidden="true"/>{kindLabel(suggestion.kind)}
            </span>
            <span>{signedMoney(suggestion.amount)}</span>
            <span className="text-gray-500">{suggestion.note}</span>
        </button>
);
SuggestionChip.displayName = "SuggestionChip";


/**
 * items x bidders - how each bid treats the scope items where they differ. B0 (this bid) first, highlighted.
 * */
function CoverageTable({leveling}: { leveling: BidLevelingState }) {
    const doc = leveling.doc;
    const items = doc?.result?.items ?? [];
    if (!doc || items.length === 0) return null;
    const bidders = doc.bids;
    return <div className="overflow-x-auto mb-2">
        <table className="text-xs border-collapse">
            <thead>
            <tr>
                <th className="text-left font-semibold pr-2">Scope item</th>
                {bidders.map((b) =>
                    <th key={b.key} title={b.company_name}
                        className={"px-1 font-normal max-w-[7rem] truncate " + (b.key === "B0" ? "bg-sky-50 font-semibold" : "")}>
                        {b.company_name}
                    </th>)}
            </tr>
            </thead>
            <tbody>
            {items.map((row, i) =>
                <tr key={i} className="border-t border-gray-100">
                    <td className="pr-2 py-0.5">{row.item}</td>
                    {bidders.map((b) => {
                        const c = row.coverage.find((x) => x.bidder === b.key);
                        const s = c ? SCOPE_STATUS[c.status] : null;
                        return <td key={b.key} className={"px-1 text-center " + (b.key === "B0" ? "bg-sky-50" : "")}>
                            {s && <span className={"rounded px-1 " + s.style} title={c?.note}>{s.label}</span>}
                        </td>
                    })}
                </tr>)}
            </tbody>
        </table>
    </div>
}


// "3 suggested plugs · 12 scope items" - the review row's header while closed
export function levelingCounts(leveling: BidLevelingState): string {
    const result = leveling.doc?.status === "done" ? leveling.doc.result : null;
    if (!result) return "";
    return ([
        [leveling.pending.length, "suggested plugs"],
        [result.items.length, "scope items"],
    ] as [number, string][]).filter(([n]) => n > 0).map(([n, label]) => `${n} ${label}`).join(" · ");
}


type LevelingDetailsProps = {
    leveling: BidLevelingState;
    onPlugsChanged: (view: PlugsView) => void;
}

/**
 * Developer only - the AI leveling result: this bid against the other bids on its scope.
 * Claude reads this bid's proposal; the others are compared from their AI reviews and SOVs.
 * Plug suggestions on SOV lines show under their lines in the SOV (BidPage); base bid ones are listed here.
 * Part of the AI review row (SmartFeedbackRow) - it runs, polls and shows status. server: backend/leveling/ai_leveling.py
 * */
export function LevelingDetails({leveling, onPlugsChanged}: LevelingDetailsProps) {
    const {doc, pending, generate, accept, reject} = leveling;
    const result = doc?.status === "done" ? doc.result : null;
    if (!doc || !result) return null;

    const bidders = doc.bids;
    const named = (text: string) => withNames(text, bidders);
    const base = pending.filter((p) => p.cell_id == null);
    const onLines = pending.length - base.length;
    const unreviewed = bidders.filter((b) => b.key !== "B0" && !b.reviewed).map((b) => b.company_name);
    const notComparable = bidders.filter((b) => !b.comparable).map((b) => b.company_name);

    return <div className="mt-3">
        <div className="font-semibold text-sm mb-1">Against the other bids</div>
        <div className="mb-2">{named(result.summary)}</div>

        <CoverageTable leveling={leveling}/>
        <NoteList title="Approach" items={result.approach.map(named)}/>
        <NoteList title="To clarify with the bidder" items={result.clarify}/>

        {pending.length > 0 &&
            <div className="mb-2">
                <div className="font-semibold">Suggested plugs</div>
                {onLines > 0 &&
                    <div className="text-muted-foreground">
                        {onLines} on SOV lines - shown under their lines in the SOV.
                    </div>}
                {base.map((s) =>
                    <div key={s.id} className={"rounded px-1 py-0.5 mt-1 " + SUGGESTION_LINE}>
                        <SuggestionPopover suggestion={s} bidders={bidders}
                                           onAccept={(edits) => accept(s, edits).then(onPlugsChanged)}
                                           onReject={() => reject(s)}>
                            <SuggestionChip suggestion={s}/>
                        </SuggestionPopover>
                    </div>)}
            </div>}

        {unreviewed.length > 0 &&
            <div className="text-muted-foreground mb-1">
                No AI review for {unreviewed.join(", ")} - compared on their bid form only.
            </div>}
        {notComparable.length > 0 &&
            <div className="text-muted-foreground mb-1">
                {notComparable.join(", ")} filled in an older SOV - not in the line comparison.
            </div>}
        {doc.file_ids.length === 0 &&
            <div className="text-muted-foreground mb-1">This bid has no documents - compared on its form only.</div>}

        <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" size="sm" onClick={generate}>Rerun comparison</Button>
        </div>
    </div>
}
